import React, { useEffect, useState } from "react";
import {
  getClientStatement,
  getClientStatementPdf,
} from "../../services/client.service";

interface StatementLine {
  date: string;
  type: "invoice" | "payment";
  reference: string;
  description: string;
  charge: number;
  payment: number;
  balance: number;
}

interface StatementSection {
  scope: "receivables" | "payables";
  opening: number;
  lines: StatementLine[];
  closing: number;
  totalCharges: number;
  totalPayments: number;
}

interface StatementResult {
  client: { name: string } | null;
  fromDate: string | null;
  toDate: string | null;
  receivables: StatementSection;
  payables: StatementSection;
}

interface Props {
  clientId: string;
  companyId: string;
  language?: any;
}

/* House tokens — shared with the Financial Reports surface. */
const C = {
  text: "#222525",
  muted: "#4A5353",
  border: "#EBF0F0",
  rule: "#9AA5A5",
  subtotalRule: "#D0D9D9",
  band: "#F7F8F8",
  rowHover: "#FAFBFB",
  danger: "#BA0E13",
  teal: "#0F7679",
  tealSoft: "#EEF5F5",
};

const NUM: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };

const t = (language: any, key: string, fallback: string): string =>
  language && language?.[key] ? language?.[key] : fallback;

/** Accounting format: thousands-separated, 2dp, negatives in parentheses. */
const fmt = (v: number | null | undefined): string => {
  const n = Number(v) || 0;
  const s = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${s})` : s;
};
const eur = (v: number | null | undefined): string => `€ ${fmt(v)}`;

const SCOPED_CSS = `
.cs-input { transition: border-color 150ms ease, box-shadow 150ms ease; }
.cs-input:focus { border-color: ${C.teal}; outline: none; box-shadow: 0 0 0 3px rgba(15,118,121,0.12); }
.cs-btn { transition: opacity 150ms ease, transform 120ms ease, background-color 150ms ease; }
.cs-btn:hover:not(:disabled) { opacity: 0.92; }
.cs-btn:active:not(:disabled) { transform: translateY(1px); }
.cs-row { transition: background-color 120ms ease; }
.cs-row:hover { background-color: ${C.rowHover}; }
`;

const ClientStatement: React.FC<Props> = ({ clientId, companyId, language }) => {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [data, setData] = useState<StatementResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  /** Localized document label from (section, line type) — independent of the
   *  backend's English description, so the UI and PDF translate identically. */
  const docLabel = (scope: StatementSection["scope"], type: StatementLine["type"]): string => {
    if (scope === "receivables") {
      return type === "invoice"
        ? t(language, "Sales invoice", "Sales invoice")
        : t(language, "Receipt", "Receipt");
    }
    return type === "invoice"
      ? t(language, "Purchase invoice", "Purchase invoice")
      : t(language, "Payment", "Payment");
  };

  /** Labels sent to the PDF endpoint so the document renders in the UI language. */
  const pdfLabels = () => ({
    title: t(language, "Statement", "Statement"),
    receivables: t(language, "Accounts Receivable", "Accounts Receivable"),
    payables: t(language, "Accounts Payable", "Accounts Payable"),
    date: t(language, "Date", "Date"),
    type: t(language, "Type", "Type"),
    reference: t(language, "Reference", "Reference"),
    charge: t(language, "Charge", "Charge"),
    payment: t(language, "Payment", "Payment"),
    balance: t(language, "Balance", "Balance"),
    opening: t(language, "Opening balance", "Opening balance"),
    closing: t(language, "Closing balance", "Closing balance"),
    period: t(language, "Period", "Period"),
    generated: t(language, "Generated", "Generated"),
    companyNo: t(language, "Company No", "Company No"),
    tax: t(language, "Tax", "Tax"),
    allTransactions: t(language, "All transactions", "All transactions"),
    salesInvoice: t(language, "Sales invoice", "Sales invoice"),
    receipt: t(language, "Receipt", "Receipt"),
    purchaseInvoice: t(language, "Purchase invoice", "Purchase invoice"),
    paymentDesc: t(language, "Payment", "Payment"),
  });

  const load = async () => {
    setLoading(true);
    try {
      const payload: any = { clientId, companyId };
      if (fromDate) payload.fromDate = fromDate;
      if (toDate) payload.toDate = toDate;
      const res: any = await getClientStatement(payload);
      setData(res.data as StatementResult);
    } catch (error) {
      console.error("Error loading statement:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const payload: any = { clientId, companyId, labels: pdfLabels() };
      if (fromDate) payload.fromDate = fromDate;
      if (toDate) payload.toDate = toDate;
      const res: any = await getClientStatementPdf(payload);
      const body = res.data;
      if (!body?.base64) return;
      const bytes = atob(body.base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = body.filename || "statement.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading statement PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const receivables = data?.receivables;
  const payables = data?.payables;
  const net = (receivables?.closing ?? 0) - (payables?.closing ?? 0);

  /* ---------- styles ---------- */
  const fieldLabel: React.CSSProperties = {
    display: "block",
    fontSize: "12px",
    color: C.muted,
    marginBottom: "4px",
  };
  const input: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${C.border}`,
    fontSize: "13px",
    color: C.text,
    background: "#FFFFFF",
  };
  const btnBase: React.CSSProperties = {
    padding: "9px 20px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid transparent",
  };
  const btnPrimary: React.CSSProperties = {
    ...btnBase,
    backgroundColor: C.teal,
    color: "#FFFFFF",
  };
  const btnGhost: React.CSSProperties = {
    ...btnBase,
    backgroundColor: "#FFFFFF",
    color: C.teal,
    border: `1px solid ${C.teal}`,
  };

  /* ---------- summary stat (one cell of the dense bar) ---------- */
  const stat = (
    label: string,
    value: number,
    helper: string,
    accent?: boolean,
  ) => (
    <div
      style={{
        padding: "16px 20px",
        backgroundColor: accent ? C.tealSoft : "#FFFFFF",
      }}
    >
      <div style={{ fontSize: "12px", color: C.muted, fontWeight: 500 }}>
        {label}
      </div>
      <div
        style={{
          ...NUM,
          fontSize: "22px",
          fontWeight: 600,
          lineHeight: 1.2,
          marginTop: "6px",
          color: value < 0 ? C.danger : C.text,
        }}
      >
        {eur(value)}
      </div>
      <div style={{ fontSize: "12px", color: C.muted, marginTop: "4px" }}>
        {helper}
      </div>
    </div>
  );

  /* ---------- one statement section table ---------- */
  const numTh: React.CSSProperties = {
    padding: "8px 14px",
    fontSize: "12px",
    fontWeight: 600,
    color: C.muted,
    textAlign: "right",
  };
  const txtTh: React.CSSProperties = { ...numTh, textAlign: "left" };
  const numTd: React.CSSProperties = {
    ...NUM,
    padding: "9px 14px",
    fontSize: "13px",
    textAlign: "right",
    whiteSpace: "nowrap",
  };
  const txtTd: React.CSSProperties = {
    padding: "9px 14px",
    fontSize: "13px",
    textAlign: "left",
  };

  const section = (title: string, subtitle: string, sec?: StatementSection) => (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        marginBottom: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "12px 16px",
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.band,
        }}
      >
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600, color: C.text }}>
            {title}
          </div>
          <div style={{ fontSize: "12px", color: C.muted, marginTop: "2px" }}>
            {subtitle}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: C.muted }}>
            {t(language, "Closing balance", "Closing balance")}
          </div>
          <div
            style={{
              ...NUM,
              fontSize: "18px",
              fontWeight: 700,
              color: (sec?.closing ?? 0) < 0 ? C.danger : C.text,
            }}
          >
            {eur(sec?.closing ?? 0)}
          </div>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            color: C.text,
          }}
        >
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.rule}` }}>
              <th style={txtTh}>{t(language, "Date", "Date")}</th>
              <th style={txtTh}>{t(language, "Type", "Type")}</th>
              <th style={txtTh}>{t(language, "Reference", "Reference")}</th>
              <th style={numTh}>{t(language, "Charge", "Charge")}</th>
              <th style={numTh}>{t(language, "Payment", "Payment")}</th>
              <th style={numTh}>{t(language, "Balance", "Balance")}</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: "#FBFCFC" }}>
              <td style={{ ...txtTd, fontWeight: 600 }} colSpan={3}>
                {t(language, "Opening balance", "Opening balance")}
              </td>
              <td style={numTd} />
              <td style={numTd} />
              <td
                style={{
                  ...numTd,
                  fontWeight: 600,
                  color: (sec?.opening ?? 0) < 0 ? C.danger : C.text,
                }}
              >
                {fmt(sec?.opening ?? 0)}
              </td>
            </tr>

            {loading && !sec
              ? [0, 1, 2, 3].map((i) => (
                  <tr key={`sk-${i}`}>
                    <td style={txtTd} colSpan={6}>
                      <div
                        style={{
                          height: "12px",
                          borderRadius: "4px",
                          backgroundColor: "#EEF2F2",
                        }}
                      />
                    </td>
                  </tr>
                ))
              : sec && sec.lines.length > 0
                ? sec.lines.map((line, idx) => (
                    <tr
                      key={idx}
                      className="cs-row"
                      style={{ borderBottom: `1px solid ${C.border}` }}
                    >
                      <td style={{ ...txtTd, ...NUM }}>{line.date}</td>
                      <td style={txtTd}>{docLabel(sec.scope, line.type)}</td>
                      <td style={{ ...txtTd, color: C.muted }}>
                        {line.reference}
                      </td>
                      <td style={numTd}>
                        {line.charge ? fmt(line.charge) : ""}
                      </td>
                      <td style={numTd}>
                        {line.payment ? fmt(line.payment) : ""}
                      </td>
                      <td
                        style={{
                          ...numTd,
                          color: line.balance < 0 ? C.danger : C.text,
                        }}
                      >
                        {fmt(line.balance)}
                      </td>
                    </tr>
                  ))
                : (
                  <tr>
                    <td
                      style={{
                        ...txtTd,
                        textAlign: "center",
                        padding: "28px 14px",
                        color: C.rule,
                      }}
                      colSpan={6}
                    >
                      {t(
                        language,
                        "No transactions in this period",
                        "No transactions in this period",
                      )}
                    </td>
                  </tr>
                )}

            <tr style={{ borderTop: `2px solid ${C.subtotalRule}` }}>
              <td style={{ ...txtTd, fontWeight: 700 }} colSpan={3}>
                {t(language, "Closing balance", "Closing balance")}
              </td>
              <td style={{ ...numTd, fontWeight: 700 }}>
                {fmt(sec?.totalCharges ?? 0)}
              </td>
              <td style={{ ...numTd, fontWeight: 700 }}>
                {fmt(sec?.totalPayments ?? 0)}
              </td>
              <td
                style={{
                  ...numTd,
                  fontWeight: 700,
                  color: (sec?.closing ?? 0) < 0 ? C.danger : C.text,
                }}
              >
                {fmt(sec?.closing ?? 0)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="font-inter" style={{ color: C.text }}>
      <style>{SCOPED_CSS}</style>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "20px",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <div style={{ flex: "0 1 170px", minWidth: "120px" }}>
          <label style={fieldLabel}>{t(language, "From", "From")}</label>
          <input
            type="date"
            className="cs-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={input}
          />
        </div>
        <div style={{ flex: "0 1 170px", minWidth: "120px" }}>
          <label style={fieldLabel}>{t(language, "To", "To")}</label>
          <input
            type="date"
            className="cs-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={input}
          />
        </div>
        <button
          className="cs-btn"
          onClick={() => !loading && load()}
          disabled={loading}
          style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}
        >
          {loading
            ? t(language, "Loading", "Loading…")
            : t(language, "Apply", "Apply")}
        </button>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "12px", color: C.muted }}>
            {t(language, "Amounts in EUR", "Amounts in EUR")}
          </span>
          <button
            className="cs-btn"
            onClick={() => !downloading && downloadPdf()}
            disabled={downloading || !data}
            style={{ ...btnGhost, opacity: downloading || !data ? 0.5 : 1 }}
          >
            {downloading
              ? t(language, "Preparing", "Preparing…")
              : t(language, "Download PDF", "Download PDF")}
          </button>
        </div>
      </div>

      {/* Dense summary bar — grid reflows to stacked on narrow screens; the
          1px gap over a border-colored background draws clean dividers in both
          orientations (columns on desktop, rows on mobile). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1px",
          backgroundColor: C.border,
          border: `1px solid ${C.border}`,
          borderRadius: "12px",
          overflow: "hidden",
          marginBottom: "24px",
        }}
      >
        {stat(
          t(language, "Accounts Receivable", "Accounts Receivable"),
          receivables?.closing ?? 0,
          t(language, "Owed by customer", "Owed by customer"),
        )}
        {stat(
          t(language, "Accounts Payable", "Accounts Payable"),
          payables?.closing ?? 0,
          t(language, "Owed to supplier", "Owed to supplier"),
        )}
        {stat(
          t(language, "Net position", "Net position"),
          net,
          net >= 0
            ? t(language, "Net owed to you", "Net owed to you")
            : t(language, "Net you owe", "Net you owe"),
          true,
        )}
      </div>

      {/* Sections */}
      {section(
        t(language, "Receivables", "Receivables"),
        t(language, "Customer account", "Customer account — sales & receipts"),
        receivables,
      )}
      {section(
        t(language, "Payables", "Payables"),
        t(language, "Supplier account", "Supplier account — purchases & payments"),
        payables,
      )}
    </div>
  );
};

export default ClientStatement;
